'use client';

import type React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { setAuthTokens } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Loader2 } from 'lucide-react';
import { userService } from '@/api/user/userApi';
import { managerService } from '@/api/manager/managerApi';
import { useAuthStore } from '@/store/authStore';
import { useFeeInitializer } from '@/hooks/useFeeInitializer';
import { handleGlobalError } from '@/utils/error-handler';
import { logError } from '@/utils/logger';
import { GoogleLogin } from '@react-oauth/google';
import AppleLoginButton from '@/components/auth/AppleLoginButton';
import { resolvePostLoginRoute } from '@/utils/post-login-route';

const Login: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { initializeFees } = useFeeInitializer();

  // The Google Identity Services button only accepts a pixel width, so we
  // measure the container and feed it the real width to match the Apple button.
  const socialRef = useRef<HTMLDivElement>(null);
  const [googleWidth, setGoogleWidth] = useState(0);

  useEffect(() => {
    const el = socialRef.current;
    if (!el) return;
    // Measure once on mount so the Google button renders even if ResizeObserver
    // is unavailable or never fires a callback.
    setGoogleWidth(Math.round(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      setGoogleWidth(Math.round(entry.contentRect.width));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { accessToken, refreshToken } = await managerService.login(
        username,
        password,
      );
      await setAuthTokens(accessToken, refreshToken);

      // Fetch user data and store it
      const userData = await userService.getCurrentUser();
      useAuthStore.setState({ user: userData });

      // Initialize fees after successful authentication
      await initializeFees();

      toast({
        title: 'Inicio de sesión exitoso',
        description: 'Bienvenido a GetMatch Administrador',
      });

      navigate(resolvePostLoginRoute(userData.roles));
    } catch (error) {
      logError('Login error', error);
      // Fixed copy here: handleGlobalError prefers API body fields, and login failures can surface
      // as 401 in different shapes (or error JSON). Avoid surfacing raw backend strings to the user.
      toast({
        variant: 'destructive',
        title: 'Error en contraseña',
        description: 'Comprueba tus datos e intenta de nuevo.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setIsLoading(true);
      const { accessToken, refreshToken } = await managerService.loginWithGoogle(
        credentialResponse.credential,
      );
      await setAuthTokens(accessToken, refreshToken);

      // Fetch user data and store it
      const userData = await userService.getCurrentUser();
      useAuthStore.setState({ user: userData });

      // Initialize fees after successful authentication
      await initializeFees();

      toast({
        title: 'Inicio de sesión exitoso',
        description: 'Bienvenido a GetMatch Administrador',
      });

      navigate(resolvePostLoginRoute(userData.roles));
    } catch (error) {
      handleGlobalError(
        error,
        'Error de inicio de sesión con Google',
        'No se pudo iniciar sesión con Google. Por favor, intenta nuevamente.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    toast({
      variant: 'destructive',
      title: 'Error',
      description: 'No se pudo iniciar sesión con Google',
    });
  };

  // Memoized so AppleLoginButton's init effect (which lists onError in its
  // deps) doesn't re-run on every Login re-render / keystroke.
  const handleAppleSuccess = useCallback(
    async (identityToken: string) => {
      try {
        setIsLoading(true);
        const { accessToken, refreshToken } =
          await managerService.loginWithApple(identityToken);
        await setAuthTokens(accessToken, refreshToken);

        // Fetch user data and store it
        const userData = await userService.getCurrentUser();
        useAuthStore.setState({ user: userData });

        // Initialize fees after successful authentication
        await initializeFees();

        toast({
          title: 'Inicio de sesión exitoso',
          description: 'Bienvenido a GetMatch Administrador',
        });

        navigate(resolvePostLoginRoute(userData.roles));
      } catch (error) {
        handleGlobalError(
          error,
          'Error de inicio de sesión con Apple',
          'No se pudo iniciar sesión con Apple. Por favor, intenta nuevamente.',
        );
      } finally {
        setIsLoading(false);
      }
    },
    [initializeFees, navigate, toast],
  );

  const handleAppleError = useCallback(() => {
    toast({
      variant: 'destructive',
      title: 'Error',
      description: 'No se pudo iniciar sesión con Apple',
    });
  }, [toast]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-primary">
      <Card className="w-full max-w-md bg-white shadow-lg">
        <CardContent className="p-8">
          <div className="mb-8 text-center">
            <img
              src="/assets/images/logo.svg"
              alt="GetMatch Logo"
              className="mx-auto mb-4 h-20 w-auto"
            />
            <h2 className="font-gilroy text-2xl font-bold text-primary">
              Administradores
            </h2>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label
                htmlFor="username"
                className="font-opensans text-sm font-semibold text-primary"
              >
                Usuario
              </Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                className="border-accent focus:border-secondary focus:ring-secondary"
                disabled={isLoading}
              />
            </div>
            <div className="space-y-2">
              <Label
                htmlFor="password"
                className="font-opensans text-sm font-semibold text-primary"
              >
                Contraseña
              </Label>
              <PasswordInput
                id="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="border-accent focus:border-secondary focus:ring-secondary"
                disabled={isLoading}
              />
            </div>
            <div className="flex justify-end">
              <Link
                to="/forgot-password"
                className="font-opensans text-sm text-primary transition-colors hover:text-secondary"
              >
                ¿Olvidaste tu contraseña?
              </Link>
            </div>

            <Button
              type="submit"
              className="w-full rounded bg-secondary font-gilroy font-medium text-white transition duration-200 hover:bg-secondary/90"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar sesión'
              )}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-white px-4 font-opensans text-gray-500">
                o continuar con
              </span>
            </div>
          </div>

          <div ref={socialRef} className="flex flex-col items-center gap-3">
            {googleWidth > 0 && (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                theme="outline"
                size="large"
                text="continue_with"
                shape="rectangular"
                width={googleWidth.toString()}
              />
            )}
            <AppleLoginButton
              onSuccess={handleAppleSuccess}
              onError={handleAppleError}
              disabled={isLoading}
            />
          </div>

          <div className="mt-6 text-center">
            <Link
              to="/register"
              className="font-opensans text-sm text-primary transition-colors hover:text-secondary"
            >
              ¿No tienes cuenta? Regístrate aquí
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
